import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { notificationService } from '../../../services/notificationService';

const NotificationManagement = ({ user, userProfile, onSuccess }) => {
  const [preferences, setPreferences] = useState({
    newSongsEnabled: true,
    newLessonsEnabled: true,
    newContentEnabled: true,
    emailEnabled: true
  });
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user?.id) {
      loadPreferences();
    }
  }, [user?.id]);

  const loadPreferences = async () => {
    try {
      setLoading(true);
      const prefs = await notificationService?.getPreferences(user?.id);
      setPreferences(prefs);
    } catch (err) {
      console.error('Error loading preferences:', err);
      setError('Failed to load notification preferences');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (key) => {
    setPreferences(prev => ({ ...prev, [key]: !prev?.[key] }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);

    try {
      await notificationService?.updatePreferences(user?.id, preferences);
      onSuccess?.('Notification preferences updated successfully!');
    } catch (err) {
      console.error('Error saving preferences:', err);
      setError('Failed to save preferences');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-12 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white" />
      </div>
    );
  }

  return (
    <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-heading font-bold text-white mb-2">
          Notification Management
        </h2>
        <p className="text-blue-100">
          Customize your notification preferences
        </p>
      </div>

      {error && (
        <div className="bg-red-50 bg-opacity-90 border border-red-200 rounded-xl p-4">
          <div className="flex items-center space-x-2">
            <Icon name="AlertTriangle" size={20} className="text-red-800" />
            <p className="text-red-800 font-medium">{error}</p>
          </div>
        </div>
      )}

      {/* Email Notifications */}
      <div className="bg-white bg-opacity-5 rounded-xl p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Icon name="Mail" size={24} className="text-blue-400" />
            <div>
              <h3 className="text-lg font-semibold text-white">Email Notifications</h3>
              <p className="text-blue-200 text-sm">Receive updates via email</p>
            </div>
          </div>
          <button
            onClick={() => handleToggle('emailEnabled')}
            className={`relative w-14 h-7 rounded-full transition-colors ${
              preferences?.emailEnabled ? 'bg-purple-600' : 'bg-gray-600'
            }`}
          >
            <span
              className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${
                preferences?.emailEnabled ? 'translate-x-7' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Notification Types */}
      <div className="space-y-3">
        <div className="bg-white bg-opacity-5 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Icon name="Music" size={20} className="text-purple-400" />
              <div>
                <p className="text-white font-medium">New Songs</p>
                <p className="text-blue-200 text-sm">Notify when new songs are added</p>
              </div>
            </div>
            <button
              onClick={() => handleToggle('newSongsEnabled')}
              disabled={!preferences?.emailEnabled}
              className={`relative w-14 h-7 rounded-full transition-colors ${
                preferences?.newSongsEnabled && preferences?.emailEnabled
                  ? 'bg-purple-600' :'bg-gray-600'
              }`}
            >
              <span
                className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${
                  preferences?.newSongsEnabled && preferences?.emailEnabled
                    ? 'translate-x-7' :'translate-x-0'
                }`}
              />
            </button>
          </div>
        </div>

        <div className="bg-white bg-opacity-5 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Icon name="BookOpen" size={20} className="text-orange-400" />
              <div>
                <p className="text-white font-medium">New Lessons</p>
                <p className="text-blue-200 text-sm">Notify when lessons become available</p>
              </div>
            </div>
            <button
              onClick={() => handleToggle('newLessonsEnabled')}
              disabled={!preferences?.emailEnabled}
              className={`relative w-14 h-7 rounded-full transition-colors ${
                preferences?.newLessonsEnabled && preferences?.emailEnabled
                  ? 'bg-purple-600' :'bg-gray-600'
              }`}
            >
              <span
                className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${
                  preferences?.newLessonsEnabled && preferences?.emailEnabled
                    ? 'translate-x-7' :'translate-x-0'
                }`}
              />
            </button>
          </div>
        </div>

        <div className="bg-white bg-opacity-5 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Icon name="Sparkles" size={20} className="text-pink-400" />
              <div>
                <p className="text-white font-medium">New Content</p>
                <p className="text-blue-200 text-sm">Stay updated on all new features</p>
              </div>
            </div>
            <button
              onClick={() => handleToggle('newContentEnabled')}
              disabled={!preferences?.emailEnabled}
              className={`relative w-14 h-7 rounded-full transition-colors ${
                preferences?.newContentEnabled && preferences?.emailEnabled
                  ? 'bg-purple-600' :'bg-gray-600'
              }`}
            >
              <span
                className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${
                  preferences?.newContentEnabled && preferences?.emailEnabled
                    ? 'translate-x-7' :'translate-x-0'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          variant="primary"
          disabled={isSaving}
          iconName="Save"
          iconPosition="left"
          className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
};

export default NotificationManagement;